import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  UsersRound, Megaphone, Trophy, Users, Pin, LinkIcon, Copy, Send, CheckCircle2,
  Crown, ShieldCheck, Trash2, LogOut, XCircle, ClipboardList, MessageSquare, Mail,
  ArrowDownWideNarrow,
} from "lucide-react";

const TEAL = "#0D9488";
const MOODS = [
  { key: "great", label: "Great", emoji: "🔥" },
  { key: "good", label: "Good", emoji: "🙂" },
  { key: "okay", label: "Okay", emoji: "😐" },
  { key: "meh", label: "Meh", emoji: "😕" },
  { key: "rough", label: "Rough", emoji: "😞" },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("feed");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${id}`);
      setGroup(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/groups");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const isCoach = group?.viewer_role === "coach";
  const isAdmin = group?.viewer_role === "admin" || user?.role === "admin";
  const canManage = isCoach || isAdmin;

  const dissolve = async () => {
    if (!window.confirm(`Dissolve "${group.name}"? Members will lose access to the group feed. This cannot be undone.`)) return;
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Group dissolved");
      navigate("/groups");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeGroup = async () => {
    if (!window.confirm(`Close "${group.name}"? Members and chat history stay visible to you for reference, but no one can post, join, or check in until you reopen it. You'll be able to create a new group after this closes.`)) return;
    try {
      await api.post(`/groups/${id}/close`);
      toast.success("Group closed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reopenGroup = async () => {
    try {
      await api.post(`/groups/${id}/reopen`);
      toast.success("Group reopened");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this group?")) return;
    try {
      await api.post(`/groups/${id}/leave`);
      toast.success("You left the group");
      navigate("/groups");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyJoinLink = () => {
    if (!group?.invite_token) return;
    const url = `${window.location.origin}/groups/join/${group.invite_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Invite link copied"));
  };

  if (loading || !group) {
    return (
      <AppLayout>
        <div className="animate-pulse h-40 bg-slate-100 rounded-xl" />
      </AppLayout>
    );
  }

  const tabs = [
    { k: "feed", label: "Feed", Icon: MessageSquare },
    canManage && { k: "dashboard", label: "Dashboard", Icon: ClipboardList },
    { k: "members", label: "Members", Icon: Users },
    { k: "checkins", label: "Check-ins", Icon: CheckCircle2 },
    canManage && { k: "settings", label: "Settings", Icon: ShieldCheck },
  ].filter(Boolean);

  return (
    <AppLayout>
      <PageHero
        eyebrow={isCoach ? "Coach view" : isAdmin ? "Admin view" : "Member view"}
        title={group.name}
        subtitle={group.description || "Private group coaching space."}
        icon={<UsersRound className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <div className="flex flex-wrap gap-2">
            {canManage && group.invite_token && !group.is_closed && !group.is_dissolved && (
              <Button
                variant="outline"
                onClick={copyJoinLink}
                className="text-teal-700 border-teal-300"
                data-testid="group-copy-invite"
              >
                <LinkIcon className="w-4 h-4 mr-1.5" /> Copy invite link
              </Button>
            )}
            {isCoach && !group.is_dissolved && !group.is_closed && (
              <Button
                variant="outline"
                onClick={closeGroup}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="group-close"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Close group
              </Button>
            )}
            {isCoach && group.is_closed && (
              <Button
                variant="outline"
                onClick={reopenGroup}
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="group-reopen"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Reopen group
              </Button>
            )}
            {isCoach && !group.is_dissolved && (
              <Button
                variant="outline"
                onClick={dissolve}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="group-dissolve"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Dissolve
              </Button>
            )}
            {isAdmin && !isCoach && !group.is_dissolved && (
              <Button
                variant="outline"
                onClick={dissolve}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="group-admin-dissolve"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Admin dissolve
              </Button>
            )}
            {!isCoach && !isAdmin && (
              <Button
                variant="outline"
                onClick={leave}
                className="border-slate-300"
                data-testid="group-leave"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Leave
              </Button>
            )}
          </div>
        }
      />

      {group.is_dissolved ? (
        <div className="mb-4 p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2" data-testid="group-dissolved-banner">
          <XCircle className="w-4 h-4" /> This group has been dissolved on {fmtDate(group.dissolved_at)}.
        </div>
      ) : group.is_closed ? (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-center gap-2" data-testid="group-closed-banner">
          <XCircle className="w-4 h-4" />
          <span>This group was closed on {fmtDate(group.closed_at)} — members and chat history are read-only. {isCoach && "Reopen to allow posting and check-ins."}</span>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 dark:border-slate-700">
        {tabs.map(({ k, label, Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 -mb-px border-b-2 text-[12px] font-mono-num uppercase tracking-widest font-semibold ${
              tab === k
                ? "border-[#0D9488] text-teal-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
            data-testid={`group-tab-${k}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === "feed" && <FeedTab group={group} isCoach={isCoach || isAdmin} onChange={load} />}
      {tab === "dashboard" && <DashboardTab groupId={id} />}
      {tab === "members" && <MembersTab groupId={id} canManage={canManage} isCoach={isCoach} onChange={load} />}
      {tab === "checkins" && <CheckinsTab group={group} isCoach={isCoach || isAdmin} />}
      {tab === "settings" && <SettingsTab group={group} onChange={load} />}
    </AppLayout>
  );
}

// ---------------- FEED ----------------
function FeedTab({ group, isCoach, onChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("post");
  const [challengeTarget, setChallengeTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${group.id}/posts`);
      setPosts(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || group.is_dissolved) return;
    setBusy(true);
    try {
      const payload = { body: body.trim(), kind };
      if (kind === "challenge") payload.challenge_target = challengeTarget.trim() || null;
      await api.post(`/groups/${group.id}/posts`, payload);
      setBody(""); setKind("post"); setChallengeTarget("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/groups/${group.id}/posts/${postId}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const complete = async (postId) => {
    try {
      await api.post(`/groups/${group.id}/challenge/${postId}/complete`);
      toast.success("Nice work — challenge completed!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-3" data-testid="group-feed">
        {!group.is_dissolved && (
          <Card className="p-4 border border-slate-200 rounded-xl bg-white">
            <form onSubmit={submit} className="space-y-3">
              <Textarea
                placeholder={isCoach ? "Post an update, announcement, or challenge…" : "Share an update, question or win with the group…"}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                maxLength={2000}
                data-testid="group-post-body"
              />
              {isCoach && (
                <div className="flex flex-wrap gap-2">
                  {[
                    { k: "post", label: "Post", Icon: MessageSquare },
                    { k: "announcement", label: "Announcement", Icon: Megaphone },
                    { k: "challenge", label: "Challenge", Icon: Trophy },
                  ].map(({ k, label, Icon }) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono-num uppercase tracking-widest font-semibold border ${
                        kind === k
                          ? "bg-[#0D9488] text-white border-[#0D9488]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"
                      }`}
                      data-testid={`group-post-kind-${k}`}
                    >
                      <Icon className="w-3 h-3" /> {label}
                    </button>
                  ))}
                </div>
              )}
              {isCoach && kind === "challenge" && (
                <Input
                  placeholder="Challenge target (e.g. Log every dose this week)"
                  value={challengeTarget}
                  onChange={(e) => setChallengeTarget(e.target.value)}
                  maxLength={120}
                  data-testid="group-post-challenge-target"
                />
              )}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={busy || !body.trim()}
                  className="text-white"
                  style={{ background: TEAL }}
                  data-testid="group-post-submit"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  {busy ? "Posting…" : "Post"}
                </Button>
              </div>
            </form>
          </Card>
        )}
        {loading ? (
          <Card className="p-6 h-24 animate-pulse bg-white" />
        ) : posts.length === 0 ? (
          <Card className="p-8 text-center border-dashed border-slate-200 rounded-xl bg-white" data-testid="group-feed-empty">
            <MessageSquare className="w-6 h-6 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">No posts yet — kick off the conversation above.</div>
          </Card>
        ) : posts.map((p) => (
          <PostRow key={p.id} p={p} group={group} onDelete={del} onComplete={complete} />
        ))}
      </div>

      {/* Pinned resources sidebar */}
      <div className="space-y-4">
        {(group.pinned_playbook_id || group.pinned_note) && (
          <Card className="p-4 border border-amber-300 bg-amber-50/40 rounded-xl" data-testid="group-pinned">
            <div className="flex items-center gap-2 mb-2">
              <Pin className="w-4 h-4 text-amber-600" />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-700">Pinned by coach</div>
            </div>
            {group.pinned_note && (
              <p className="text-sm text-slate-700 mb-2 whitespace-pre-wrap">{group.pinned_note}</p>
            )}
            {group.pinned_playbook_id && (
              <Link
                to={`/playbook/${group.pinned_playbook_id}${group.pinned_playbook_lesson_id ? `#lesson-${group.pinned_playbook_lesson_id}` : ""}`}
                className="inline-flex items-center gap-1 text-teal-700 font-semibold text-sm hover:text-teal-900"
                data-testid="group-pinned-link"
              >
                Open playbook →
              </Link>
            )}
          </Card>
        )}
        <Card className="p-4 border border-slate-200 rounded-xl bg-white">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono-num font-semibold mb-2">Group</div>
          <div className="text-sm text-slate-700 space-y-1">
            <div>{group.member_count} / {group.max_members} members</div>
            <div>Coached by <b>{group.coach?.name || "—"}</b></div>
            <div className="text-xs text-slate-500">Created {fmtDate(group.created_at)}</div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function PostRow({ p, group, onDelete, onComplete }) {
  const isAnnouncement = p.kind === "announcement";
  const isChallenge = p.kind === "challenge";
  return (
    <Card
      className={`p-4 border rounded-xl bg-white ${
        isAnnouncement ? "border-amber-300 bg-amber-50/40" : isChallenge ? "border-teal-300 bg-teal-50/30" : "border-slate-200"
      }`}
      data-testid={`group-post-${p.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xs"
          style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
        >
          {(p.author?.name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-sm text-slate-900">{p.author?.name || "Someone"}</span>
            {p.author?.is_coach && (
              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-amber-700">
                <Crown className="w-2.5 h-2.5" /> Coach
              </span>
            )}
            {isAnnouncement && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700">
                <Megaphone className="w-2.5 h-2.5" /> Announcement
              </span>
            )}
            {isChallenge && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-100 text-teal-700">
                <Trophy className="w-2.5 h-2.5" /> Challenge
              </span>
            )}
            <span className="text-[11px] text-slate-500 ml-auto">{fmt(p.created_at)}</span>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.body}</p>
          {isChallenge && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {p.challenge_target && (
                <span className="text-[11px] font-mono-num uppercase tracking-widest text-teal-700 bg-teal-100 px-2 py-0.5 rounded">
                  Target: {p.challenge_target}
                </span>
              )}
              <span className="text-[11px] text-slate-500">
                {p.challenge_completed_count} completed
              </span>
              {p.challenge_completed_by_me ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700" data-testid={`group-challenge-done-${p.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> You completed this
                </span>
              ) : (
                <Button
                  size="sm"
                  onClick={() => onComplete(p.id)}
                  className="text-white h-7"
                  style={{ background: TEAL }}
                  data-testid={`group-challenge-complete-${p.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark complete
                </Button>
              )}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => onDelete(p.id)}
              className="text-[10px] text-slate-400 hover:text-red-600 uppercase tracking-widest font-mono-num"
              data-testid={`group-post-delete-${p.id}`}
            >
              <Trash2 className="w-3 h-3 inline mr-0.5" /> Delete
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------- DASHBOARD (coach view of members) ----------------
function DashboardTab({ groupId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem("amino_group_dashboard_sort_v1") || "countdown"; }
    catch { return "countdown"; }
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/groups/${groupId}/dashboard`)
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    try { localStorage.setItem("amino_group_dashboard_sort_v1", sortKey); } catch {}
  }, [sortKey]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    // Always keep the coach at the top
    const nameSort = (a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
    if (sortKey === "countdown") {
      // Overdue first — a countdown < 0 or null-with-no-shot goes on top.
      // Rank: negative countdown (most overdue first) → positive countdown ascending → null (no shot yet)
      arr.sort((a, b) => {
        if (a.role === "coach" && b.role !== "coach") return -1;
        if (b.role === "coach" && a.role !== "coach") return 1;
        const aC = a.countdown_hours;
        const bC = b.countdown_hours;
        // null = never logged a shot; treat as "very overdue" so they surface
        const aRank = aC === null || aC === undefined ? -Infinity : aC;
        const bRank = bC === null || bC === undefined ? -Infinity : bC;
        return aRank - bRank;
      });
    } else if (sortKey === "last_shot") {
      arr.sort((a, b) => {
        if (a.role === "coach" && b.role !== "coach") return -1;
        if (b.role === "coach" && a.role !== "coach") return 1;
        const at = a.last_shot_at ? new Date(a.last_shot_at).getTime() : 0;
        const bt = b.last_shot_at ? new Date(b.last_shot_at).getTime() : 0;
        return bt - at;
      });
    } else {
      arr.sort((a, b) => {
        if (a.role === "coach" && b.role !== "coach") return -1;
        if (b.role === "coach" && a.role !== "coach") return 1;
        return nameSort(a, b);
      });
    }
    return arr;
  }, [rows, sortKey]);

  if (loading) return <Card className="p-6 h-40 animate-pulse bg-white" />;
  if (rows.length === 0) return <Card className="p-8 text-center bg-white border-dashed border-slate-200">No members yet.</Card>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <ArrowDownWideNarrow className="w-3.5 h-3.5 text-slate-500" />
        <label className="text-[10px] uppercase tracking-widest font-mono-num font-semibold text-slate-500">Sort</label>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="h-8 rounded border border-slate-200 bg-white text-xs px-2 font-mono-num"
          data-testid="group-dashboard-sort"
        >
          <option value="countdown">Countdown (overdue first)</option>
          <option value="last_shot">Last shot (recent first)</option>
          <option value="name">Name (A→Z)</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="group-dashboard">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Member</th>
              <th className="text-left p-3">Last shot</th>
              <th className="text-left p-3">Countdown</th>
              <th className="text-left p-3">Last weight</th>
              <th className="text-left p-3">Week calories</th>
              <th className="text-left p-3">Meals logged</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.user_id} className="border-t border-slate-100" data-testid={`group-dashboard-row-${r.user_id}`}>
                <td className="p-3">
                  <div className="font-semibold text-slate-900">{r.name || "—"} {r.role === "coach" && <Crown className="w-3 h-3 inline text-amber-600" />}</div>
                  <div className="text-[11px] text-slate-500">@{r.handle || "—"}</div>
                </td>
                <td className="p-3 font-mono-num text-slate-700">{r.last_shot_at ? fmt(r.last_shot_at) : <span className="text-slate-400">—</span>}</td>
                <td className="p-3 font-mono-num">
                  {r.countdown_hours === null || r.countdown_hours === undefined ? <span className="text-slate-400">—</span>
                    : r.countdown_hours < 0 ? <span className="text-red-600 font-semibold">Overdue {Math.abs(r.countdown_hours).toFixed(1)}h</span>
                    : <span className="text-teal-700 font-semibold">{r.countdown_hours.toFixed(1)}h</span>}
                </td>
                <td className="p-3 font-mono-num text-slate-700">{r.last_weight ? `${r.last_weight} ${r.weight_unit}` : <span className="text-slate-400">—</span>}</td>
                <td className="p-3 font-mono-num text-slate-700">{r.week_calories?.toLocaleString() || 0}</td>
                <td className="p-3 font-mono-num text-slate-700">{r.week_meals_logged || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- MEMBERS ----------------
function MembersTab({ groupId, canManage, isCoach, onChange }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [group, setGroup] = useState(null);
  // Only phones can open the SMS app. Desktop browsers can't, so we hide the button.
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /Android|iPhone|iPad|iPod|Windows Phone|Mobile|BlackBerry|Opera Mini/i.test(ua);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, groupRes] = await Promise.all([
        api.get(`/groups/${groupId}/members`),
        api.get(`/groups/${groupId}`),
      ]);
      setRows(membersRes.data.items || []);
      setGroup(groupRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (uid, name) => {
    if (!window.confirm(`Remove ${name || uid} from the group?`)) return;
    try {
      await api.delete(`/groups/${groupId}/members/${uid}`);
      toast.success("Member removed");
      load();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <Card className="p-4 border border-slate-200 rounded-xl bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="font-display font-bold text-slate-900 text-sm">Invite new members</div>
            <div className="text-xs text-slate-500">
              Opens a draft in your own email or messaging app — you send it personally from your device.
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <Button
              onClick={() => setInviteOpen(true)}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="group-invite-open"
            >
              <Mail className="w-4 h-4 mr-1.5" /> Invite by email
            </Button>
            {isMobile && (
              <Button
                onClick={() => setSmsOpen(true)}
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="group-invite-sms-open"
              >
                <MessageSquare className="w-4 h-4 mr-1.5" /> Invite by text
              </Button>
            )}
          </div>
        </Card>
      )}
      {loading ? (
        <Card className="p-6 h-24 animate-pulse bg-white" />
      ) : (
        <Card className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="group-members">
          <ul className="divide-y divide-slate-100">
            {rows.map((m) => (
              <li key={m.user_id} className="p-3 flex items-center gap-3" data-testid={`group-member-${m.user_id}`}>
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xs"
                  style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
                >
                  {(m.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {m.name || m.email || m.handle}
                    {m.role === "coach" && <Crown className="w-3.5 h-3.5 text-amber-600 inline ml-1" />}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {m.email} · joined {fmtDate(m.joined_at)}
                  </div>
                </div>
                {canManage && m.role !== "coach" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => remove(m.user_id, m.name)}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid={`group-member-remove-${m.user_id}`}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {group && (
        <MailtoInviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          group={group}
          coachName={user?.name || "your coach"}
        />
      )}
      {group && (
        <SmsInviteDialog
          open={smsOpen}
          onClose={() => setSmsOpen(false)}
          group={group}
          coachName={user?.name || "your coach"}
        />
      )}
    </div>
  );
}

// ---------------- MAILTO INVITE ----------------
function MailtoInviteDialog({ open, onClose, group, coachName }) {
  const joinLink = group?.invite_token
    ? `${window.location.origin}/groups/join/${group.invite_token}`
    : "";
  const defaultSubject = `You have been invited to join my coaching group on The Amino Exchange`;
  const defaultBody = `Hi there,

${coachName} here — I'd like to invite you to join my private coaching group "${group?.name}" on The Amino Exchange.

Inside the group we share weekly check-ins, wins, and challenges. It's a small, private space so we can move faster together.

Tap the link below to join (you'll need a free account first if you don't have one):
${joinLink}

Looking forward to having you in.

— ${coachName}`;
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  // Reset on open with fresh coach/group context.
  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setBody(defaultBody);
    }
    // eslint-disable-next-line
  }, [open, group?.id, coachName]);

  const sendViaMailto = () => {
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // Some browsers block window.open for mailto — use assign fallback.
    try {
      window.location.href = url;
    } catch {
      window.open(url, "_blank");
    }
    toast.success("Opening your email app…");
    onClose?.();
  };

  const copyLink = () => {
    if (!joinLink) return;
    navigator.clipboard.writeText(joinLink).then(() => toast.success("Join link copied"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="group-invite-mailto">
        <DialogHeader>
          <DialogTitle>Compose email invite</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Edit the subject and message, then hit Send. Your own email app opens with the draft ready — <b>the app never sends anything on your behalf</b>. You add the recipient and send from your own inbox.
          </p>
          <div>
            <Label htmlFor="mailto-subject">Subject</Label>
            <Input
              id="mailto-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              data-testid="group-invite-subject"
            />
          </div>
          <div>
            <Label htmlFor="mailto-body">Message</Label>
            <Textarea
              id="mailto-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="font-mono-num text-[13px]"
              data-testid="group-invite-body"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              The private join link for this group is already pasted into the message.
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono-num">
            <button
              type="button"
              onClick={copyLink}
              className="text-teal-700 hover:text-teal-900 underline"
              data-testid="group-invite-copy-link"
            >
              Copy join link only
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={sendViaMailto}
            disabled={!subject.trim() || !body.trim() || !joinLink}
            className="text-white"
            style={{ background: TEAL }}
            data-testid="group-invite-send"
          >
            <Send className="w-4 h-4 mr-1.5" /> Open in email app
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- SMS INVITE (mobile only) ----------------
function SmsInviteDialog({ open, onClose, group, coachName }) {
  const joinLink = group?.invite_token
    ? `${window.location.origin}/groups/join/${group.invite_token}`
    : "";
  // Plain text only, no formatting, only one link — the join link.
  const defaultBody = `Hey — ${coachName} here. I'd like to invite you to my private coaching group "${group?.name}" on The Amino Exchange. Tap to join: ${joinLink}`;
  const [body, setBody] = useState(defaultBody);

  useEffect(() => {
    if (open) setBody(defaultBody);
    // eslint-disable-next-line
  }, [open, group?.id, coachName]);

  const sendViaSms = () => {
    // sms:?body=... works on iOS 14+ and Android. Some older iOS versions want
    // sms:&body=... — the double-question-mark form is broadly tolerated too.
    const encoded = encodeURIComponent(body);
    // Detect iOS to prefer the ?& form, otherwise use ?body=
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    const url = isIOS ? `sms:&body=${encoded}` : `sms:?body=${encoded}`;
    try {
      window.location.href = url;
    } catch {
      window.open(url, "_blank");
    }
    toast.success("Opening your messages app…");
    onClose?.();
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(body).then(() => toast.success("Message copied"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="group-invite-sms">
        <DialogHeader>
          <DialogTitle>Compose text invite</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Edit the message, then tap Send. Your phone's messages app opens with the draft ready — <b>the app never sends anything on your behalf</b>. You add the phone number and send from your own device.
          </p>
          <div>
            <Label htmlFor="sms-body">Message</Label>
            <Textarea
              id="sms-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              maxLength={500}
              className="font-mono-num text-[13px]"
              data-testid="group-invite-sms-body"
            />
            <div className="flex items-center justify-between mt-1">
              <div className="text-[10px] text-slate-400">
                Plain text only. The join link is the only link.
              </div>
              <div className="text-[10px] font-mono-num text-slate-500" data-testid="group-invite-sms-count">
                {body.length} / 500
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono-num">
            <button
              type="button"
              onClick={copyMessage}
              className="text-teal-700 hover:text-teal-900 underline"
              data-testid="group-invite-sms-copy"
            >
              Copy message
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={sendViaSms}
            disabled={!body.trim() || !joinLink}
            className="text-white"
            style={{ background: TEAL }}
            data-testid="group-invite-sms-send"
          >
            <Send className="w-4 h-4 mr-1.5" /> Open in messages app
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- CHECK-INS ----------------
function CheckinsTab({ group, isCoach }) {
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [mood, setMood] = useState("good");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [mineRes, sumRes] = await Promise.all([
        api.get(`/groups/${group.id}/checkin/me`),
        isCoach ? api.get(`/groups/${group.id}/checkins/summary`) : Promise.resolve({ data: null }),
      ]);
      setMe(mineRes.data);
      if (mineRes.data?.mood) setMood(mineRes.data.mood);
      if (mineRes.data?.note) setNote(mineRes.data.note);
      setSummary(sumRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [group.id, isCoach]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/groups/${group.id}/checkin`, { mood, note: note.trim() || null });
      toast.success("Thanks for checking in this week!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-5 border border-slate-200 rounded-xl bg-white" data-testid="group-checkin-mine">
        <div className="text-[11px] uppercase tracking-widest font-mono-num font-semibold text-teal-700 mb-2">
          Weekly check-in
        </div>
        <div className="font-display font-bold text-lg text-slate-900 mb-1">How are you feeling this week?</div>
        <div className="text-xs text-slate-500 mb-4">
          {me?.submitted
            ? `You've already checked in on ${fmtDate(me.submitted_at)}. Update your answer if you want.`
            : "One tap to answer. Your coach will see the roll-up."}
        </div>
        <div className="grid grid-cols-5 gap-2 mb-3" data-testid="group-checkin-moods">
          {MOODS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMood(m.key)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition ${
                mood === m.key ? "border-[#0D9488] bg-teal-50" : "border-slate-200 hover:border-teal-300"
              }`}
              data-testid={`group-checkin-mood-${m.key}`}
            >
              <span className="text-xl" aria-hidden>{m.emoji}</span>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-600">{m.label}</span>
            </button>
          ))}
        </div>
        <Textarea
          placeholder="Anything to add? (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={500}
          data-testid="group-checkin-note"
        />
        <div className="mt-3 flex justify-end">
          <Button
            onClick={submit}
            disabled={busy || group.is_dissolved}
            className="text-white"
            style={{ background: TEAL }}
            data-testid="group-checkin-submit"
          >
            {busy ? "Saving…" : me?.submitted ? "Update check-in" : "Submit check-in"}
          </Button>
        </div>
      </Card>

      {isCoach && summary && (
        <Card className="p-5 border border-slate-200 rounded-xl bg-white" data-testid="group-checkin-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono-num font-semibold text-teal-700">
                Week of {summary.week_start}
              </div>
              <div className="font-display font-bold text-lg text-slate-900">
                {summary.submitted_count} of {summary.total} checked in
              </div>
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {summary.items.map((r) => {
              const emo = MOODS.find((m) => m.key === r.mood)?.emoji;
              return (
                <li key={r.user_id} className="py-2 flex items-center gap-3">
                  <div className="text-2xl w-8 text-center">{emo || "·"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{r.name || r.handle}</div>
                    {r.note && <div className="text-xs text-slate-500 italic line-clamp-1">"{r.note}"</div>}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-mono-num font-semibold text-slate-400">
                    {r.submitted ? "Done" : "Pending"}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ---------------- SETTINGS ----------------
function SettingsTab({ group, onChange }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || "");
  const [pinNote, setPinNote] = useState(group.pinned_note || "");
  const [pinPlaybook, setPinPlaybook] = useState(group.pinned_playbook_id || "");
  const [playbooks, setPlaybooks] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/playbooks")
      .then(({ data }) => setPlaybooks(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setPlaybooks([]));
  }, []);

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.patch(`/groups/${group.id}`, {
        name: name.trim(),
        description: description.trim(),
      });
      toast.success("Saved");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const savePin = async () => {
    setBusy(true);
    try {
      await api.patch(`/groups/${group.id}/pin`, {
        playbook_id: pinPlaybook || null,
        lesson_id: null,
        note: pinNote.trim() || null,
      });
      toast.success(pinPlaybook || pinNote.trim() ? "Pinned resource updated" : "Pin cleared");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-5 border border-slate-200 rounded-xl bg-white space-y-3" data-testid="group-settings-meta">
        <div className="font-display font-bold text-slate-900 mb-1">Group info</div>
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="group-settings-name" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={600} data-testid="group-settings-desc" />
        </div>
        <div className="pt-1">
          <Button onClick={saveMeta} disabled={busy} className="text-white" style={{ background: TEAL }} data-testid="group-settings-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 rounded-xl bg-white space-y-3" data-testid="group-settings-pin">
        <div className="flex items-center gap-2 mb-1">
          <Pin className="w-4 h-4 text-amber-600" />
          <div className="font-display font-bold text-slate-900">Pinned resource</div>
        </div>
        <div className="text-xs text-slate-500 mb-2">Pin one of your playbooks or a short note to the top of the group feed.</div>
        <div>
          <Label htmlFor="pin-pb">Playbook</Label>
          <select
            id="pin-pb"
            value={pinPlaybook}
            onChange={(e) => setPinPlaybook(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-mono-num"
            data-testid="group-settings-pin-playbook"
          >
            <option value="">— None —</option>
            {playbooks.map((p) => (
              <option key={p.id} value={p.id}>{p.title || p.name || "Untitled"}</option>
            ))}
          </select>
          {playbooks.length === 0 && (
            <div className="text-[10px] text-slate-400 mt-1">No playbooks available yet. Create one from the Playbook page first.</div>
          )}
        </div>
        <div>
          <Label>Note</Label>
          <Textarea value={pinNote} onChange={(e) => setPinNote(e.target.value)} rows={3} maxLength={400} placeholder="e.g. 'Read this before Monday's call'" data-testid="group-settings-pin-note" />
        </div>
        <div>
          <Button onClick={savePin} disabled={busy} className="text-white" style={{ background: TEAL }} data-testid="group-settings-pin-save">
            {busy ? "Saving…" : "Save pin"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
